import { useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import type { CounterHotspotSpec } from "../scenario/types";

/** Screen-space pin (percent of viewport) for a counter hotspot's anchor. */
export type CounterHotspotPin = { xPct: number; yPct: number; visible: boolean; distance: number };

/**
 * Invisible world-space anchor for a walkable-room click prompt (e.g. "Order
 * a drink" near the bartender, ending free-roam). Same screen-projection
 * technique as EntryIntercom/DeskPackage/the lounge laptop's sticky note
 * (PrivacyRoomProps.tsx), just with no visible 3D geometry of its own — the
 * room's real props (the bartender, the counter) already read fine; this only
 * reports WHERE on screen the DOM prompt/label belongs.
 */
export function CounterHotspot({
  spec,
  onPin,
}: {
  spec: CounterHotspotSpec;
  onPin: (pin: CounterHotspotPin) => void;
}) {
  const { camera } = useThree();
  const worldPos = useRef(new THREE.Vector3(...spec.position)).current;
  const camDir = useRef(new THREE.Vector3()).current;
  const toPin = useRef(new THREE.Vector3()).current;
  const last = useRef<CounterHotspotPin>({ xPct: 0, yPct: 0, visible: false, distance: 999 });
  const onPinRef = useRef(onPin);
  onPinRef.current = onPin;
  const maxDistance = spec.maxDistance ?? 6;

  useFrame(() => {
    camera.getWorldDirection(camDir);
    toPin.copy(worldPos).sub(camera.position);
    const distance = toPin.length();
    const inFront = toPin.normalize().dot(camDir) > 0.1;
    const proj = worldPos.clone().project(camera);
    const xPct = (proj.x * 0.5 + 0.5) * 100;
    const yPct = (-proj.y * 0.5 + 0.5) * 100;
    const onScreen = xPct > -5 && xPct < 105 && yPct > -5 && yPct < 105;
    const pin: CounterHotspotPin = { xPct, yPct, visible: inFront && onScreen && distance < maxDistance, distance };
    const p = last.current;
    if (
      p.visible !== pin.visible ||
      Math.abs(p.xPct - pin.xPct) > 0.3 ||
      Math.abs(p.yPct - pin.yPct) > 0.3 ||
      Math.abs(p.distance - pin.distance) > 0.05
    ) {
      last.current = pin;
      onPinRef.current(pin);
    }
  });

  return null;
}
